/**
 * Scout v0 — criteria composer flow (P16, task 1–4).
 *
 * A four-step operator wizard:
 *   1. build   — layer-1 summary (read-only) + layer-2 template picker/editor +
 *                layer-3 specifics form. "Compose" merges the layers (no AI).
 *   2. confirm — for loose roles, review/confirm/edit/reject AI synonym proposals.
 *   3. preview — the FULL composed spec exactly as it will be recorded. The ONLY
 *                place the run can be launched.
 *   4. result  — the launched run's funnel counts + graded leads.
 *
 * LAUNCH GATING: the launch mutation is wired ONLY from the preview step, and
 * the preview's Launch button is disabled while any loose role is unconfirmed.
 * There is no UI path that launches without first showing the preview — the
 * backend also rejects unconfirmed specs (409), but the UI never offers a
 * bypass.
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ClassTemplate,
  ComposedCriteria,
  ConfirmedRoleEdit,
  CriteriaPreview,
  Layer3Specifics,
  LaunchResponse,
} from '@/types/scout';
import {
  scoutQueryKeys,
  useCompose,
  useConfirm,
  useLaunch,
  useScoutProfile,
} from '@/lib/scout-queries';
import { getErrorMessage } from '@/lib/queries';
import { useToast } from '@/components/ui/useToast';
import { ErrorBanner } from '@/components/ui';
import { Layer1Summary } from './Layer1Summary';
import { TemplatePicker } from './TemplatePicker';
import { SpecificsForm } from './SpecificsForm';
import { SynonymConfirm } from './SynonymConfirm';
import { PreviewPanel } from './PreviewPanel';
import { LaunchResult } from './LaunchResult';

type Step = 'build' | 'confirm' | 'preview' | 'result';

const STEP_ORDER: { key: Step; label: string }[] = [
  { key: 'build', label: 'Build' },
  { key: 'confirm', label: 'Confirm synonyms' },
  { key: 'preview', label: 'Preview & launch' },
];

function Stepper({ current }: { current: Step }) {
  // "result" reads as past the final step.
  const activeIndex = current === 'result' ? STEP_ORDER.length : STEP_ORDER.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-2 mb-6" aria-label="Composer progress">
      {STEP_ORDER.map((s, i) => {
        const state = i < activeIndex ? 'done' : i === activeIndex ? 'current' : 'todo';
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={`flex items-center justify-center h-6 w-6 rounded-full text-xs font-medium ${
                state === 'done'
                  ? 'bg-primary text-bg'
                  : state === 'current'
                    ? 'bg-secondary text-white'
                    : 'bg-surface text-muted border border-border'
              }`}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span className={`text-sm ${state === 'todo' ? 'text-muted' : 'text-text'}`}>{s.label}</span>
            {i < STEP_ORDER.length - 1 && <span className="text-muted mx-1">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function ComposeFlow() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const profile = useScoutProfile();
  const compose = useCompose();
  const confirm = useConfirm();
  const launch = useLaunch();

  const [step, setStep] = useState<Step>('build');

  // Build-step inputs. `hasSelectedRoles` = at least one layer-2 template role
  // is picked in the specifics form (layer 3 selects roles FROM the template).
  const [selectedTemplate, setSelectedTemplate] = useState<ClassTemplate | null>(null);
  const [specifics, setSpecifics] = useState<Layer3Specifics>({});
  const [hasSelectedRoles, setHasSelectedRoles] = useState(false);

  // Composed spec + preview (authoritative; replaced on confirm).
  const [composed, setComposed] = useState<ComposedCriteria | null>(null);
  const [preview, setPreview] = useState<CriteriaPreview | null>(null);

  // Confirm-step state.
  const [confirmedEdits, setConfirmedEdits] = useState<ConfirmedRoleEdit[]>([]);
  const [allConfirmed, setAllConfirmed] = useState(false);

  // Result.
  const [launchResult, setLaunchResult] = useState<LaunchResponse | null>(null);

  const templateHasRoles = (selectedTemplate?.roles.length ?? 0) > 0;
  const canCompose = Boolean(selectedTemplate) && hasSelectedRoles;

  const specSummary = useMemo(() => {
    if (!selectedTemplate) return null;
    const geo = specifics.geo ?? [];
    const roles = specifics.roles ?? [];
    return { name: selectedTemplate.name, geoCount: geo.length, roleCount: roles.length };
  }, [selectedTemplate, specifics]);

  /* ── Step transitions ─────────────────────────────────────────────────── */

  const handleCompose = () => {
    if (!selectedTemplate) return;
    compose.mutate(
      { templateId: selectedTemplate.id, specifics },
      {
        onSuccess: (res) => {
          setComposed(res.composed);
          setPreview(res.preview);
          setConfirmedEdits([]);
          setAllConfirmed(false);
          setStep('confirm');
        },
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  const handleContinueToPreview = () => {
    if (!composed || !selectedTemplate) return;
    confirm.mutate(
      { composed, edits: confirmedEdits, templateId: selectedTemplate.id },
      {
        onSuccess: (res) => {
          setComposed(res.composed);
          setPreview(res.preview);
          setStep('preview');
          if (res.persistedSynonyms.length > 0) {
            // Confirmed synonyms were persisted to the layer-2 template.
            queryClient.invalidateQueries({ queryKey: scoutQueryKeys.templates });
          }
        },
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  const handleLaunch = () => {
    if (!composed || !selectedTemplate) return;
    launch.mutate(
      { composed, templateId: selectedTemplate.id },
      {
        onSuccess: (res) => {
          setLaunchResult(res);
          setStep('result');
          addToast('success', 'Run launched.');
        },
        onError: (err) => addToast('error', getErrorMessage(err)),
      },
    );
  };

  const backToBuild = () => {
    setStep('build');
    setComposed(null);
    setPreview(null);
    setConfirmedEdits([]);
    setAllConfirmed(false);
  };

  const composeAnother = () => {
    backToBuild();
    setSelectedTemplate(null);
    setSpecifics({});
    setHasSelectedRoles(false);
    setLaunchResult(null);
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  return (
    <div>
      <Stepper current={step} />

      {step === 'build' && (
        <div className="space-y-6">
          <Layer1Summary profile={profile.data} isLoading={profile.isLoading} />

          <TemplatePicker selectedId={selectedTemplate?.id ?? null} onSelect={setSelectedTemplate} />

          <SpecificsForm
            templateRoles={selectedTemplate?.roles ?? []}
            templateId={selectedTemplate?.id ?? null}
            onChange={setSpecifics}
            onValidityChange={setHasSelectedRoles}
            disabled={compose.isPending}
          />

          {compose.isError && (
            <div className="space-y-2">
              <ErrorBanner
                message={getErrorMessage(compose.error)}
                retryable={compose.error?.retryable}
                onRetry={handleCompose}
              />
              {compose.error?.code === 'IDENTITY_MISSING' && (
                <p className="text-sm text-warning">
                  Every composed search needs the always-on workspace identity (layer 1).{' '}
                  <Link to="/scout/suppressions" className="underline">
                    Set it in Workspace Criteria
                  </Link>
                  , then compose again.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted">
              {selectedTemplate
                ? hasSelectedRoles
                  ? 'Ready to compose.'
                  : templateHasRoles
                    ? 'Pick at least one template role to compose.'
                    : 'The selected template has no roles - edit it to add its role blanket.'
                : 'Select a class template to compose.'}
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={handleCompose}
              disabled={!canCompose || compose.isPending}
            >
              {compose.isPending ? 'Composing…' : 'Compose criteria'}
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && composed && selectedTemplate && (
        <div className="space-y-6">
          {specSummary && (
            <p className="text-xs text-muted">
              Composing <span className="text-text font-medium">{specSummary.name}</span> ·{' '}
              {specSummary.roleCount} role(s) selected · {specSummary.geoCount} geo
            </p>
          )}

          <SynonymConfirm
            composed={composed}
            onEditsChange={setConfirmedEdits}
            onAllConfirmedChange={setAllConfirmed}
            disabled={confirm.isPending}
          />

          {confirm.isError && (
            <ErrorBanner
              message={getErrorMessage(confirm.error)}
              retryable={confirm.error?.retryable}
              onRetry={handleContinueToPreview}
            />
          )}

          <div className="flex items-center justify-between">
            <button type="button" className="btn-ghost" onClick={backToBuild} disabled={confirm.isPending}>
              ← Back
            </button>
            <div className="flex items-center gap-3">
              {!allConfirmed && (
                <span className="text-xs text-warning">Confirm all loose roles to continue.</span>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={handleContinueToPreview}
                disabled={!allConfirmed || confirm.isPending}
              >
                {confirm.isPending ? 'Preparing…' : 'Continue to preview'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <PreviewPanel
          preview={preview}
          onLaunch={handleLaunch}
          onBack={() => setStep('confirm')}
          launching={launch.isPending}
          launchError={launch.isError ? getErrorMessage(launch.error) : null}
          onRetry={handleLaunch}
        />
      )}

      {step === 'result' && launchResult && (
        <LaunchResult result={launchResult} onComposeAnother={composeAnother} />
      )}
    </div>
  );
}
